import * as React from "react";
import { findDOMNode } from "react-dom";
import { AllIconNames, FxInput, Input as BaseInput, Textarea as BaseTextArea, ZIndex } from "react-ui";
import debounce from "lodash.debounce";
import { RenderContainer } from "@skbkontur/react-ui/internal/RenderContainer";

import { useScrollTo } from "../../Internals/Contexts/ScrollToContext";
import { MetricsValueSenderActions } from "../../Internals/Metrics/MetricsValueSender";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { emptyRef } from "../../TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { LayoutEvents } from "../../Internals/LayoutEvents/LayoutEvents";
import { themed } from "../../Internals/ThemeProvider/ThemedWorkaround";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { DataPasteContext, DatePasteStatus } from "../../MultiControls/MultiLine/TableDataPasteContext";
import { ValueEditorsFocusManager } from "../../../Helpers/JumpByEnterOnNextControl/ValueEditorsFocusManager";
import { delay } from "../../../Engine/utils";
import { Text } from "../../ValueViewers/Text/Text";
import { cutExtraSpacesFromPaste, cutExtraSpaces } from "../../../Helpers/CutExtraSpaces";

import * as styles from "./PopupTextArea.styles";
import { DistanceToHorizontalBorders, PopupHelper, Rect } from "./PopupHelper";
import { PopupContainer } from "./PopupTextArea.styles";
import { PopupTextAreaTestPageObject } from "./PopupTextAreaTestPageObject";
import { PopupTextAreaWithAuto } from "./PopupTextAreaWithAuto";

export interface PopupTextAreaProps extends ControlTooltipProps, ControlValidationProps {
    bindingPath: AbsoluteModelPath;
    value?: string;
    onChange: (nextValue: string) => void;
    placeholder?: string;
    disabled?: boolean;
    disabled2?: boolean;
    isTooltipEnabled?: boolean;
    tooltip?: string;
    width?: string | number;
    popupWidth?: string | number;
    rows?: number;
    resize?: React.CSSProperties["resize"];
    autoResize?: boolean;
    metrics: MetricsValueSenderActions;
    textMaxLength?: number | undefined;
    excelPastePrevent?: boolean;
    isReadOnly?: boolean;
    isLink?: boolean;
    useAutoIcon?: AllIconNames;
    auto?: boolean;
    autoFlag?: boolean;
    autoValue?: string;
    onClick?: () => void;
    "data-tid"?: string;
}

export function PopupTextArea(props: PopupTextAreaProps): JSX.Element {
    const container = React.useRef<HTMLDivElement>(emptyRef);
    const popupContainer = React.useRef<HTMLDivElement>(emptyRef);
    const input = React.useRef<BaseInput>(emptyRef);
    const textarea = React.useRef<BaseTextArea>(emptyRef);
    const scrollTo = useScrollTo();
    const processTableDataPaste = React.useContext(DataPasteContext);

    useTestPageObject(
        () => PopupTextAreaTestPageObject(props.value, textarea, container, props.onChange),
        [props.value, textarea, container]
    );

    const { error, tooltip, warning, isTooltipEnabled } = props;
    const ownTooltipContent = error || warning || tooltip;
    const showTooltip = ownTooltipContent != undefined && ownTooltipContent !== "" && isTooltipEnabled;
    const tooltipTrigger = showTooltip ? "hover&focus" : "closed";
    const tooltipPosition = props.tooltipPosition ?? "top left";

    const [open, setOpen] = React.useState(false);
    const [blinking, setBlinking] = React.useState(false);
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [coords, setPopupCoords] = React.useState({ left: 0, top: 0 });

    const popupAbortController = React.useRef(new AbortController());
    const popupPreviewDelay: number = 100;

    const blink = () => {
        if (!blinking) {
            setBlinking(true);
            setTimeout(() => setBlinking(false), 150);
        }
    };

    const handleFocus = () => {
        updateCoords();
        setOpen(true);

        props.metrics.saveValue(props.value);
    };

    const handleTextAreaKeyDown = (event: React.KeyboardEvent) => {
        if (input.current == undefined) {
            return;
        }
        if (event.key === "Tab") {
            const currentInputDomElement = findDOMNode(input.current);
            if (currentInputDomElement == undefined || !(currentInputDomElement instanceof HTMLElement)) {
                return;
            }
            const inputNode = currentInputDomElement.querySelector("input");
            if (inputNode == undefined) {
                return;
            }
            const nextEditableElement = event.shiftKey
                ? ValueEditorsFocusManager.findPreviousValueEditor(inputNode)
                : ValueEditorsFocusManager.findNextValueEditor(inputNode);
            if (nextEditableElement == undefined) {
                return;
            }
            event.preventDefault();
            ValueEditorsFocusManager.setFocusOnControl(nextEditableElement);
        }
    };

    const handleRequestFocus = () => {
        if (container.current != undefined) {
            scrollTo(container.current);
        }
        updateCoords();
        setOpen(true);
        setTimeout(() => {
            if (textarea.current == undefined) {
                return;
            }
            textarea.current.focus();
            textarea.current.selectAll();
        }, 0);
    };

    const handleFocusInput = () => {
        updateCoords();
        setOpen(true);
        setTimeout(() => textarea.current?.focus(), 0);
    };

    const handleChange = (nextValue: string) => {
        const isPasteOrSomeExternalChange = (from: string, to: string) => Math.abs(from.length - to.length) > 5;

        if (props.textMaxLength != undefined && props.value != undefined) {
            const prevValueValid = props.value.length <= props.textMaxLength;
            if (prevValueValid) {
                // Ну мы типа не будем блокировать ввод если содержимое меняется резко:
                // Это может быть
                //   * встравка из буфера, пользователь может хочет вставить и подправить
                //   * какие-нибудь плагины автоматом вставляют что-нибудь, не будем им мешать
                if (!isPasteOrSomeExternalChange(props.value, nextValue)) {
                    if (nextValue.length > props.textMaxLength) {
                        blink();
                        return;
                    }
                }
            } else {
                // Не дадим печатать если пользовать как-то получил большой текст и продолжает
                // его увеличивать, а стирать -- можно
                const nextValueIncreasesLength = nextValue.length > props.value.length;
                if (nextValueIncreasesLength && !isPasteOrSomeExternalChange(props.value, nextValue)) {
                    blink();
                    return;
                }
            }
        }
        props.onChange(nextValue);
    };

    const showPopup = async (signal: AbortSignal) => {
        signal.onabort = () => setPreviewOpen(false);
        await delay(popupPreviewDelay);
        if (!signal.aborted) {
            updateCoords();
            setPreviewOpen(true);
        }
    };

    const handleMouseEnter = async () => {
        popupAbortController.current = new AbortController();
        await showPopup(popupAbortController.current.signal);
    };

    const mouseLeave = async () => {
        popupAbortController.current.abort();
    };

    const handleBlur = () => {
        closePopup();
        const formattedText = cutExtraSpaces(props.value);
        props.onChange(formattedText);
        props.metrics.sendValue(props.bindingPath, formattedText);
    };

    const handlePaste = (event: React.ClipboardEvent<unknown>) => {
        if (!props.excelPastePrevent) {
            const formattedText = cutExtraSpacesFromPaste({ event: event, previousText: props.value });
            props.onChange(formattedText);
            event.preventDefault();
        } else {
            const result = processTableDataPaste(event.clipboardData, {
                bindingPath: props.bindingPath,
            });
            if (result !== DatePasteStatus.Failure) {
                event.preventDefault();
            }
        }
    };

    const closePopup = () => setOpen(false);

    const getPopupWidth = () => {
        if (popupContainer?.current != undefined) {
            const popupContainerRect = PopupHelper.getElementAbsoluteRect(popupContainer.current);
            return popupContainerRect.width;
        }
        return 0;
    };

    const calculatePopupLeftPositionWithShift = (rect: Rect, distanceToBorders: DistanceToHorizontalBorders) =>
        distanceToBorders.toLeft > -distanceToBorders.toRight
            ? rect.left + distanceToBorders.toRight
            : rect.left - distanceToBorders.toLeft;

    const updateCoords = () => {
        if (container?.current != undefined) {
            const rect = PopupHelper.getElementAbsoluteRect(container.current);
            rect.width = getPopupWidth();
            const isAbsoluteRectTopUnderTopBorder = PopupHelper.isAbsoluteRectTopUnderTopBorder(rect);

            const distanceToBorders = PopupHelper.getDistanceToHorizontalBorders(rect);
            if (distanceToBorders.toRight < 0) {
                rect.left = calculatePopupLeftPositionWithShift(rect, distanceToBorders);
            }

            const coordsChanged = rect.left !== coords.left || rect.top !== coords.top;
            if (coordsChanged || !isAbsoluteRectTopUnderTopBorder) {
                setOpen(false);
                setPreviewOpen(false);
            }
            if (coordsChanged && isAbsoluteRectTopUnderTopBorder) {
                setPopupCoords(rect);
            }
        }
    };

    const inPopup = previewOpen || open;

    React.useLayoutEffect(() => {
        if (container?.current != undefined) {
            const rect = PopupHelper.getElementAbsoluteRect(container.current);
            if (rect.left !== coords.left || rect.top !== coords.top) {
                setPopupCoords(rect);
            }
        }
    }, []);

    React.useEffect(() => {
        if (inPopup) {
            const layoutEventsSubscription = LayoutEvents.addListener(debounce(updateCoords, 100));
            return () => layoutEventsSubscription.remove();
        }
        return undefined;
    }, [inPopup]);

    const renderTextLengthInfo = () => {
        if (props.textMaxLength == undefined) {
            return undefined;
        }
        return (
            <span css={styles.textLengthInfo(inPopup)}>
                <span css={styles.currentLength((props.value?.length ?? 0) > props.textMaxLength)}>
                    {props.value?.length ?? 0}
                </span>
                {" / "}
                {props.textMaxLength.toString()}
            </span>
        );
    };

    if (props.isReadOnly) {
        return (
            <Text
                value={props.value}
                bindingPath={props.bindingPath}
                isLink={props.isLink}
                data-tid={props["data-tid"]}
                onClick={props.onClick}
            />
        );
    }

    return (
        <FocusManagementObserver onRequestFocus={handleRequestFocus}>
            <Tooltip
                trigger={inPopup && showTooltip ? "opened" : tooltipTrigger}
                value={ownTooltipContent}
                useWrapper={false}
                width={props.tooltipWidth}
                pos={tooltipPosition}>
                <div
                    css={styles.container(props.width)}
                    ref={container}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={mouseLeave}>
                    {!props.auto ? (
                        <BaseInput
                            ref={input}
                            data-tid={props["data-tid"]}
                            disabled={props.disabled}
                            error={Boolean(error)}
                            warning={Boolean(warning)}
                            value={props.value}
                            width={props.width}
                            onFocus={handleFocusInput}
                            onValueChange={handleChange}
                        />
                    ) : (
                        <FxInput
                            auto={props.auto && props.autoFlag}
                            useAutoIcon={props.useAutoIcon}
                            data-tid={props["data-tid"]}
                            disabled={props.disabled}
                            error={Boolean(error)}
                            warning={Boolean(warning)}
                            value={props.value}
                            width={props.width}
                            onFocus={handleFocusInput}
                            onValueChange={handleChange}
                        />
                    )}
                    {inPopup ? (
                        <>
                            <RenderContainer>
                                <ZIndex
                                    style={{
                                        position: "absolute",
                                        top: coords.top,
                                        left: coords.left,
                                    }}>
                                    <PopupContainer ref={popupContainer}>
                                        <PopupTextAreaWithAuto popupTextAreaProps={props}>
                                            <BaseTextArea
                                                css={themed(blinking && styles.blink)}
                                                ref={textarea}
                                                data-tid={props["data-tid"]}
                                                placeholder={props.placeholder}
                                                width={props.popupWidth}
                                                disabled={props.disabled}
                                                error={Boolean(error)}
                                                warning={Boolean(warning)}
                                                value={props.value}
                                                rows={props.rows}
                                                resize={props.resize}
                                                autoResize={true}
                                                onValueChange={handleChange}
                                                onFocus={handleFocus}
                                                onBlur={handleBlur}
                                                onKeyDown={handleTextAreaKeyDown}
                                                onPaste={handlePaste}
                                            />
                                            {renderTextLengthInfo()}
                                        </PopupTextAreaWithAuto>
                                    </PopupContainer>
                                </ZIndex>
                            </RenderContainer>
                        </>
                    ) : (
                        renderTextLengthInfo()
                    )}
                </div>
            </Tooltip>
        </FocusManagementObserver>
    );
}
